import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import type { AdjustBackofficeOrganizationBalanceInput } from '../types';

type AdjustmentTab = AdjustBackofficeOrganizationBalanceInput['direction'];

export interface AdjustOrganizationBalanceModalProps {
  organizationName: string;
  submitting?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (input: AdjustBackofficeOrganizationBalanceInput) => Promise<void> | void;
}

function getTitle(direction: AdjustmentTab): string {
  return direction === 'increase' ? 'Increase balance' : 'Decrease balance';
}

export function AdjustOrganizationBalanceModal({
  organizationName,
  submitting = false,
  error = null,
  onClose,
  onSubmit,
}: AdjustOrganizationBalanceModalProps) {
  const [activeTab, setActiveTab] = useState<AdjustmentTab>('increase');
  const [amount, setAmount] = useState('1');
  const [internalComment, setInternalComment] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || submitting) {
        return;
      }

      if (confirmOpen) {
        setConfirmOpen(false);
      } else {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [confirmOpen, onClose, submitting]);

  const parsedAmount = useMemo(() => Number.parseInt(amount, 10), [amount]);
  const amountError =
    Number.isInteger(parsedAmount) && parsedAmount > 0
      ? null
      : 'Enter a whole number greater than zero.';
  const trimmedInternalComment = internalComment.trim();
  const internalCommentMissing = !trimmedInternalComment;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (amountError || internalCommentMissing || submitting) {
      return;
    }

    setConfirmOpen(true);
  }

  async function handleConfirmAdjustment() {
    if (amountError || internalCommentMissing || submitting) {
      return;
    }

    await onSubmit({
      direction: activeTab,
      amount: parsedAmount,
      internalComment: trimmedInternalComment,
    });
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/45 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="adjust-balance-title"
        className="w-full max-w-xl rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-neutral-200 px-6 py-5">
          <div>
            <h2
              id="adjust-balance-title"
              className="font-inter-tight text-xl font-semibold text-neutral-900"
            >
              Adjust balance
            </h2>
            <p className="mt-1 text-body text-neutral-500">{organizationName}</p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="rounded-md p-2 text-neutral-500 hover:bg-backoffice-50 hover:text-neutral-900 disabled:cursor-not-allowed disabled:text-neutral-300"
            aria-label="Close"
            onClick={onClose}
            disabled={submitting}
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>

        <div className="border-b border-neutral-200 px-6 pt-4">
          <div className="flex gap-5">
            {(['increase', 'decrease'] as const).map((direction) => (
              <button
                key={direction}
                type="button"
                className={[
                  'border-b-2 px-1 pb-3 text-body font-semibold',
                  activeTab === direction
                    ? 'border-neutral-900 text-neutral-900'
                    : 'border-transparent text-neutral-500 hover:text-backoffice-600',
                ].join(' ')}
                aria-current={activeTab === direction ? 'page' : undefined}
                onClick={() => setActiveTab(direction)}
                disabled={submitting}
              >
                {getTitle(direction)}
              </button>
            ))}
          </div>
        </div>

        <form className="px-6 py-5" onSubmit={handleSubmit}>
          <label className="flex flex-col gap-2 text-small font-medium text-neutral-700">
            Interview credits
            <input
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              className="h-11 rounded-lg border border-neutral-200 bg-white px-3 text-body text-neutral-900"
            />
          </label>

          <p className="mt-3 text-small text-neutral-500">
            {activeTab === 'increase'
              ? 'This adds credits to the organization interview balance.'
              : 'This removes credits from the organization interview balance.'}
          </p>

          <label
            htmlFor="adjust-balance-comment"
            className="mt-5 flex flex-col gap-2 text-small font-medium text-neutral-700"
          >
            Internal comment
            <textarea
              id="adjust-balance-comment"
              required
              rows={4}
              value={internalComment}
              onChange={(event) => setInternalComment(event.target.value)}
              className="min-h-24 resize-y rounded-lg border border-neutral-200 bg-white px-3 py-2 text-body text-neutral-900"
            />
          </label>

          {amountError ? <p className="mt-3 text-small text-red-700">{amountError}</p> : null}
          <p className="mt-2 text-small text-neutral-500">
            Required internal note for audit context. This is not shown to the customer.
          </p>
          {error ? <p className="mt-3 text-small text-red-700">{error}</p> : null}

          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={Boolean(amountError) || internalCommentMissing || submitting}
              className="focus-ring inline-flex h-10 items-center rounded-lg bg-backoffice-500 px-4 text-body font-medium text-white hover:bg-backoffice-600 disabled:cursor-not-allowed disabled:bg-backoffice-300"
            >
              {submitting ? (
                <span className="inline-flex items-center whitespace-nowrap">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  &nbsp;Saving adjustment
                </span>
              ) : (
                getTitle(activeTab)
              )}
            </button>
          </div>
        </form>

        {confirmOpen ? (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-neutral-950/45 p-4"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !submitting) {
                setConfirmOpen(false);
              }
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="confirm-adjust-balance-title"
              className="w-full max-w-md rounded-xl bg-white shadow-2xl"
            >
              <div className="flex items-center justify-between gap-4 border-b border-neutral-200 px-6 py-4">
                <h3
                  id="confirm-adjust-balance-title"
                  className="font-inter-tight text-xl font-semibold text-neutral-900"
                >
                  Confirm balance adjustment
                </h3>
                <button
                  type="button"
                  onClick={() => setConfirmOpen(false)}
                  className="rounded-md p-2 text-neutral-500 hover:bg-backoffice-50 hover:text-neutral-900 disabled:cursor-not-allowed disabled:text-neutral-300"
                  aria-label="Close"
                  disabled={submitting}
                >
                  <X className="size-5" aria-hidden="true" />
                </button>
              </div>
              <div className="px-6 py-5">
                <dl className="space-y-3 text-body">
                  <div className="flex justify-between gap-4">
                    <dt className="text-neutral-500">Organization</dt>
                    <dd className="font-medium text-neutral-900">{organizationName}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-neutral-500">Adjustment</dt>
                    <dd className="font-medium text-neutral-900">
                      {activeTab === 'increase' ? '+' : '-'}
                      {parsedAmount} interview {parsedAmount === 1 ? 'credit' : 'credits'}
                    </dd>
                  </div>
                </dl>
                <p className="mt-5 text-body text-neutral-700">
                  The new transaction will be saved and can be viewed by the user, while the
                  internal comment is kept for audit context.
                </p>
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={handleConfirmAdjustment}
                    disabled={submitting}
                    className="focus-ring inline-flex h-10 items-center rounded-lg bg-backoffice-500 px-4 text-body font-medium text-white hover:bg-backoffice-600 disabled:cursor-not-allowed disabled:bg-backoffice-300"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
}
